import React, { useEffect, useRef, useState } from "react";
import learningDocService from "../services/learningDocService";
import LoadingSpinner from "./common/LoadingSpinner";
import Modal, {
  ModalPrimaryButton,
  ModalSecondaryButton,
} from "./common/Modal";

// The enforced cap, matching spring.servlet.multipart.max-file-size and
// app.learning-docs.max-file-size in the backend.
const MAX_UPLOAD_MB = 100;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

// What we tell users. Deliberately lower than the enforced cap so that a file
// somewhat over the stated limit still uploads instead of failing - nothing
// below MAX_UPLOAD_MB is ever rejected.
const DISPLAY_MAX_UPLOAD_MB = 60;

// The backend stores links in three fixed slots (url1..url3).
const MAX_LINKS = 3;
const URL_PATTERN = /^https?:\/\/.+/;
// Long enough that we are not saving on every keystroke, short enough that a
// user who types a link and immediately closes the tab keeps it.
const AUTOSAVE_DELAY_MS = 800;

const emptyLink = () => ({ url: "", alias: "" });

const isBlankLink = (link) => !link.url.trim() && !link.alias.trim();

/** Stable identity for "have these links changed since the last save?". */
function serializeLinks(links) {
  return JSON.stringify(
    links.map((link) => [link.url.trim(), link.alias.trim()]),
  );
}

/** Flattens the list back into the six positional arguments the API expects. */
function toRequestArgs(links) {
  return Array.from({ length: MAX_LINKS }, (_, i) => links[i]).flatMap(
    (link) => [link?.url.trim() || null, link?.alias.trim() || null],
  );
}

/** Inline autosave feedback, in place of a Save button. */
function SaveStatus({ state }) {
  if (state === "idle") return null;

  const view = {
    pending: { icon: "fa-pen", text: "Unsaved changes...", tone: "text-slate-cloud" },
    saving: {
      icon: "fa-circle-notch fa-spin",
      text: "Saving...",
      tone: "text-slate-cloud",
    },
    saved: { icon: "fa-check", text: "All changes saved", tone: "text-green-700" },
    failed: {
      icon: "fa-triangle-exclamation",
      text: "Could not save - your changes are still here",
      tone: "text-sunrise-red",
    },
  }[state];

  return (
    <p className={`text-sm flex items-center gap-2 ${view.tone}`} role="status">
      <i className={`fas ${view.icon}`}></i>
      {view.text}
    </p>
  );
}

function LearningDocumentation() {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  // Links are held as a list rather than six flat fields: exactly the rows the
  // user has added, in order. A new row appears only when they ask for one, so
  // the section stays compact.
  const [links, setLinks] = useState([]);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [urlError, setUrlError] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [removeLinkConfirm, setRemoveLinkConfirm] = useState(null);
  // "idle" | "pending" | "saving" | "saved" | "failed"
  const [saveState, setSaveState] = useState("idle");

  // Autosave must not fire for the values we just loaded from the server, and
  // must not re-send a payload that is already stored.
  const hydratedRef = useRef(false);
  const lastSavedRef = useRef(null);

  useEffect(() => {
    fetchLearningDocumentation();
  }, []);

  const fetchLearningDocumentation = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await learningDocService.getMyLearningDocumentation();
      const loaded = [1, 2, 3]
        .map((n) => ({
          url: data[`learningDocumentationUrl${n}`] || "",
          alias: data[`learningDocumentationAlias${n}`] || "",
        }))
        .filter((link) => link.url.trim());
      setLinks(loaded);
      lastSavedRef.current = serializeLinks(loaded);
      hydratedRef.current = true;
      setFiles(data.files || []);
    } catch (err) {
      console.error("Error fetching learning documentation:", err);
      setError("Failed to load learning documentation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const savedLinks = links.filter((link) => link.url.trim());
  const hasInvalidUrl = links.some(
    (link) => link.url.trim() && !URL_PATTERN.test(link.url.trim()),
  );

  const addLink = () => {
    setLinks((prev) =>
      prev.length < MAX_LINKS ? [...prev, emptyLink()] : prev,
    );
  };

  const updateLink = (index, field, value) => {
    setLinks((prev) =>
      prev.map((link, i) => (i === index ? { ...link, [field]: value } : link)),
    );

    if (field === "url") {
      setUrlError((prev) => {
        const next = { ...prev };
        if (value.trim() && !URL_PATTERN.test(value.trim())) {
          next[index] = "Invalid URL. It must start with http:// or https://";
        } else {
          delete next[index];
        }
        return next;
      });
    }
  };

  const removeLink = (index) => {
    setLinks((prev) => prev.filter((_, i) => i !== index));
    // Errors are keyed by position, so they no longer line up once a row goes.
    setUrlError({});
    setRemoveLinkConfirm(null);
  };

  /**
   * A row the user just added and never filled in has nothing to lose, so it is
   * dropped straight away. Only a real link is worth a confirmation.
   */
  const requestRemoveLink = (index) => {
    if (isBlankLink(links[index])) {
      removeLink(index);
    } else {
      setRemoveLinkConfirm(index);
    }
  };

  const saveLinks = async (payload, snapshot) => {
    try {
      setSaveState("saving");
      setError(null);
      await learningDocService.updateLearningDocUrl(...payload);
      lastSavedRef.current = snapshot;
      setSaveState("saved");
    } catch (err) {
      console.error("Error saving URLs:", err);
      setSaveState("failed");
      setError(
        err.response?.data?.message ||
          "Failed to save links. Your changes are still here - retry or reload.",
      );
    }
  };

  // Autosave: persist a moment after typing stops so links are never lost to a
  // forgotten Save button or a closed tab.
  useEffect(() => {
    if (!hydratedRef.current || hasInvalidUrl) return undefined;

    const snapshot = serializeLinks(savedLinks);
    if (snapshot === lastSavedRef.current) return undefined;

    setSaveState("pending");
    const timer = setTimeout(
      () => saveLinks(toRequestArgs(savedLinks), snapshot),
      AUTOSAVE_DELAY_MS,
    );
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializeLinks(savedLinks), hasInvalidUrl]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reject oversize files before uploading them: without this the whole file
    // is transferred only to be rejected by the server.
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(
        `The file is too large (${formatFileSize(file.size)}). The maximum upload size is ${MAX_UPLOAD_MB} MB.`,
      );
      event.target.value = "";
      return;
    }

    try {
      setUploading(true);
      setError(null);
      const newFile = await learningDocService.uploadFile(file);
      setFiles([newFile, ...files]);
      setSuccess("File uploaded successfully!");
      setTimeout(() => setSuccess(null), 3000);
      // Reset file input
      event.target.value = "";
    } catch (err) {
      console.error("Error uploading file:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to upload file. Please try again.";
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      setDeleting(true);
      setError(null);
      await learningDocService.deleteFile(fileId);
      setFiles(files.filter((f) => f.id !== fileId));
      setSuccess("File deleted successfully!");
      setTimeout(() => setSuccess(null), 3000);
      setDeleteConfirm(null);
    } catch (err) {
      console.error("Error deleting file:", err);
      setError("Failed to delete file. Please try again.");
      setDeleteConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      setError(null);
      await learningDocService.downloadFile(fileId, fileName);
    } catch (err) {
      console.error("Error downloading file:", err);
      setError("Failed to download file. Please try again.");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("de-CH", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <LoadingSpinner
        message="Loading your learning documentation..."
        size="lg"
      />
    );
  }

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="card-brand-elevated p-8 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
                  <i className="fas fa-book text-sunrise-red mr-4"></i>
                  Learning Documentation
                </h1>

                <p className="text-lg text-slate-cloud">
                  Manage your external learning documentation link and upload
                  related files in one place.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Feedback Messages */}
        {success && (
          <div className="mb-6">
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl shadow-brand flex items-start">
              <i className="fas fa-check-circle text-green-500 mr-3 mt-0.5"></i>
              <p className="text-sm">{success}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl shadow-brand flex items-start">
              <i className="fas fa-exclamation-triangle text-red-500 mr-3 mt-0.5"></i>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="space-y-6">
          {/* Link Card */}
          <div className="bg-ice-white rounded-xl shadow-brand border border-pale-dawn p-6">
            <h2 className="text-xl font-bold text-grey-dusk mb-4 flex items-center">
              <i className="fas fa-link text-sunrise-red mr-3"></i>
              Learning Documentation Links
            </h2>
            <p className="text-sm text-slate-cloud mb-4">
              Add up to 3 external links to your learning documentation (for example cloud folders or portfolio sites).
            </p>

            <div className="space-y-4">
              {links.map((link, index) => {
                return (
                  <div
                    key={index}
                    className="border border-pale-dawn rounded-lg p-4 bg-white"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-grey-dusk">
                        Link {index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => requestRemoveLink(index)}
                        className="text-sm text-sunrise-red hover:underline flex items-center gap-1.5"
                        aria-label={`Remove link ${index + 1}`}
                      >
                        <i className="fas fa-times"></i>
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor={`docUrl${index}`}
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          URL
                        </label>
                        <input
                          type="url"
                          id={`docUrl${index}`}
                          value={link.url}
                          onChange={(e) =>
                            updateLink(index, "url", e.target.value)
                          }
                          placeholder="https://example.com/my-documentation"
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-sunrise-red focus:border-transparent text-sm ${
                            urlError[index] ? "border-red-500" : "border-gray-300"
                          }`}
                        />
                        {urlError[index] && (
                          <p className="text-red-600 text-sm mt-1 flex items-center">
                            <i className="fas fa-exclamation-circle mr-1"></i>
                            {urlError[index]}
                          </p>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor={`docAlias${index}`}
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Display name (optional)
                        </label>
                        <input
                          type="text"
                          id={`docAlias${index}`}
                          value={link.alias}
                          onChange={(e) =>
                            updateLink(index, "alias", e.target.value)
                          }
                          placeholder="e.g. OneDrive folder"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sunrise-red focus:border-transparent text-sm"
                        />
                      </div>
                    </div>

                    {URL_PATTERN.test(link.url.trim()) && (
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sunrise-red hover:text-red-700 underline break-all text-sm inline-flex items-center mt-3"
                      >
                        <i className="fas fa-external-link-alt mr-2"></i>
                        {link.alias || link.url}
                      </a>
                    )}
                  </div>
                );
              })}

              {links.length === 0 && (
                <p className="text-sm text-slate-cloud">
                  You have not added any links yet.
                </p>
              )}

              {links.length < MAX_LINKS ? (
                <button
                  type="button"
                  onClick={addLink}
                  className="btn-brand-outline text-sm inline-flex items-center gap-2"
                >
                  <i className="fas fa-plus"></i>
                  {links.length === 0 ? "Add link" : "Add another link"}
                </button>
              ) : (
                <p className="text-sm text-slate-cloud flex items-center gap-2">
                  <i className="fas fa-circle-info text-sunrise-red"></i>
                  You have reached the maximum of {MAX_LINKS} links. Remove one
                  to add another.
                </p>
              )}

              <SaveStatus state={saveState} />
            </div>
          </div>

          {/* Files Card */}
          <div className="bg-ice-white rounded-xl shadow-brand border border-pale-dawn p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
              <h2 className="text-xl font-bold text-grey-dusk flex items-center">
                <i className="fas fa-file-upload text-sunrise-red mr-3"></i>
                Learning Documentation Files
              </h2>
              <label
                htmlFor="fileUpload"
                className={`bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center text-sm ${
                  uploading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-plus mr-2"></i>
                    Upload File
                  </>
                )}
                <input
                  type="file"
                  id="fileUpload"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  accept=".pdf,image/png,image/jpeg"
                  className="hidden"
                />
              </label>
            </div>

            <p className="text-gray-600 mb-4 text-sm">
              Upload your learning documentation files (PDF, PNG, JPEG). Maximum
              file size: {DISPLAY_MAX_UPLOAD_MB} MB. Maximum 10 files.
            </p>

            {/* Files List */}
            {files.length > 0 ? (
              <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        File Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Size
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Upload Date
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {files.map((file) => (
                      <tr key={file.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            <i
                              className={`fas ${
                                file.mimeType === "application/pdf"
                                  ? "fa-file-pdf text-red-500"
                                  : "fa-file-image text-red-500"
                              } mr-2`}
                            ></i>
                            <span className="text-sm font-medium text-gray-900">
                              {file.fileName}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {formatFileSize(file.sizeBytes)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(file.createdAt)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() =>
                              handleDownloadFile(file.id, file.fileName)
                            }
                            className="text- hover:text-red-700 mr-4 inline-flex items-center"
                          >
                            <i className="fas fa-download mr-1"></i>
                            Download
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(file.id)}
                            className="text-red-600 hover:text-red-900 inline-flex items-center"
                          >
                            <i className="fas fa-trash mr-1"></i>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <i className="fas fa-folder-open text-5xl mb-4"></i>
                <p className="text-sm font-medium">No files uploaded yet</p>
                <p className="text-xs mt-2">
                  Click &quot;Upload File&quot; to add your first document
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Remove Link Confirmation */}
      <Modal
        isOpen={removeLinkConfirm !== null}
        onClose={() => setRemoveLinkConfirm(null)}
        title="Remove Link"
        icon="fa-link-slash"
        tone="danger"
        size="md"
        footer={
          <>
            <ModalSecondaryButton onClick={() => setRemoveLinkConfirm(null)}>
              Cancel
            </ModalSecondaryButton>
            <ModalPrimaryButton
              tone="danger"
              onClick={() => removeLink(removeLinkConfirm)}
            >
              <i className="fas fa-times"></i>
              Remove
            </ModalPrimaryButton>
          </>
        }
      >
        <p className="text-sm text-grey-dusk">
          Are you sure you want to remove this link? The change is saved
          straight away.
        </p>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={Boolean(deleteConfirm)}
        onClose={() => setDeleteConfirm(null)}
        title="Delete File"
        icon="fa-trash-alt"
        tone="danger"
        size="md"
        footer={
          <>
            <ModalSecondaryButton onClick={() => setDeleteConfirm(null)}>
              Cancel
            </ModalSecondaryButton>
            <ModalPrimaryButton
              tone="danger"
              loading={deleting}
              loadingText="Deleting..."
              onClick={() => handleDeleteFile(deleteConfirm)}
            >
              <i className="fas fa-trash"></i>
              Delete
            </ModalPrimaryButton>
          </>
        }
      >
        <p className="text-sm text-grey-dusk">
          Are you sure you want to delete this file? This action cannot be
          undone.
        </p>
      </Modal>
    </div>
  );
}

export default LearningDocumentation;
